import { useCallback, useEffect, useRef, useState } from 'react';

export const AUTOSAVE_DELAY_MS = 1500;

/**
 * Guardado con debounce para "Entrenar". Sólo guarda cuando alguien llamó `markDirty` (un cambio del
 * alumno): la precarga nunca lo hace, así abrir la pantalla no registra un entrenamiento que no
 * hubo. Nunca hay dos guardados a la vez: si llega un cambio mientras uno está en curso, se manda
 * al terminar éste, con el estado más reciente (`save` lee siempre los datos actuales).
 */
export function useAutosave({ save, onError, enabled, delayMs = AUTOSAVE_DELAY_MS }) {
  const [status, setStatus] = useState('idle');
  const saveRef = useRef(save);
  const onErrorRef = useRef(onError);
  const enabledRef = useRef(enabled);
  saveRef.current = save;
  onErrorRef.current = onError;
  enabledRef.current = enabled;

  const timer = useRef(null);
  const dirty = useRef(false);
  const locked = useRef(false);
  const running = useRef(null);

  const clearTimer = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const run = useCallback(() => {
    clearTimer();
    if (running.current) return running.current;
    if (!dirty.current || !enabledRef.current) return Promise.resolve(true);
    dirty.current = false;
    setStatus('saving');
    const promise = (async () => {
      let ok = true;
      let handledFailure = false;
      try {
        await saveRef.current();
      } catch (error) {
        ok = false;
        const handled = onErrorRef.current ? onErrorRef.current(error) : false;
        if (handled) {
          // Already dealt with (e.g. 401 -> login): nothing left to retry, callers may move on.
          handledFailure = true;
          setStatus('idle');
        } else {
          dirty.current = true;
          setStatus('error');
        }
      }
      running.current = null;
      if (ok) {
        if (dirty.current) return run();
        setStatus('saved');
      }
      return ok || handledFailure;
    })();
    running.current = promise;
    return promise;
  }, [clearTimer]);

  const markDirty = useCallback(() => {
    if (!enabledRef.current || locked.current) return;
    dirty.current = true;
    clearTimer();
    timer.current = setTimeout(() => { run(); }, delayMs);
  }, [clearTimer, delayMs, run]);

  const flush = useCallback(() => run(), [run]);

  const settle = useCallback(async () => {
    locked.current = true;
    clearTimer();
    if (running.current) await running.current;
    dirty.current = false;
  }, [clearTimer]);

  const unlock = useCallback(() => { locked.current = false; }, []);

  const reset = useCallback(() => {
    clearTimer();
    dirty.current = false;
    locked.current = false;
    setStatus('idle');
  }, [clearTimer]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  return { status, markDirty, flush, settle, unlock, reset };
}
